import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * Prescription card / OPD card — an A5 landscape sheet the doctor uses when
 * writing on paper. Layout:
 *
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │ [Hospital header — name, address, phones, GST, logo]         │
 *   ├─────────────────────────────────────────────────────────────┤
 *   │ UHID │ Name              │ Age/Sex │ Date/Time             │
 *   │ Mob  │ Doctor            │ Dept    │ Payer                 │
 *   ├─────────────────────────────────────────────────────────────┤
 *   │ Complaints / Notes:                                          │
 *   │   __________________________________________________________ │
 *   │   __________________________________________________________ │
 *   │                                                              │
 *   │ Rx:                                                          │
 *   │   __________________________________________________________ │
 *   │   __________________________________________________________ │
 *   │                                                              │
 *   │                                    Doctor's Signature ______ │
 *   └─────────────────────────────────────────────────────────────┘
 *
 * URL: `/print/prescription-card/<patientId>?doctor=<Dr. Name>&autoprint=0`
 */
export default function PrintPrescriptionCard() {
  const { patientId } = useParams();
  const [search] = useSearchParams();
  const doctor = search.get("doctor") || "—";
  const autoprint = search.get("autoprint") !== "0";
  // Letterhead mode — when true, the hospital header is suppressed so the
  // card can be printed on the hospital's pre-printed letterhead paper. The
  // identity panel + all clinical sections still render normally.
  const [letterhead, setLetterhead] = useState(search.get("letterhead") === "1");

  const [patient, setPatient] = useState(null);
  const [hospital, setHospital] = useState(null);
  const [branch, setBranch] = useState(null);
  const [settings, setSettings] = useState({
    paper_size: "A4", orientation: "portrait",
    margin_top: 6, margin_right: 7, margin_bottom: 6, margin_left: 7,
    show_header: true, content_scale: 100,
  });

  useEffect(() => {
    (async () => {
      const me = await api.get("/auth/me");
      setHospital(me.data.hospital);
      setBranch(me.data.branch);
      const { data } = await api.get(`/patients/${patientId}`);
      setPatient(data.patient || data);
      // Hospital admin can override the printed layout — margins, paper, header.
      // The URL `?letterhead=1` and admin settings both flip show_header off.
      try {
        const { data: s } = await api.get("/hospital/settings/prescription-card");
        setSettings((prev) => ({ ...prev, ...s }));
      } catch { /* fall through with defaults */ }
      if (autoprint) setTimeout(() => window.print(), 500);
    })();
  }, [patientId, autoprint]);

  if (!patient) return <div className="p-8 text-sm text-ink-muted">Loading card…</div>;

  const ts = patient.created_at ? new Date(patient.created_at) : new Date();
  const dateStr = ts.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  const timeStr = ts.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
  const ageSex = [
    patient.age != null && patient.age !== "" ? `${patient.age} yr` : "",
    patient.gender || "",
  ].filter(Boolean).join(" / ") || "—";

  // A hospital logo may or may not exist depending on branding setup.
  const logo = hospital?.logo_url || branch?.logo_url || "";

  // Header shown when settings say so AND the URL/admin toggle isn't forcing
  // letterhead mode.
  const showHeader = settings.show_header && !letterhead;
  const paperSize = `${settings.paper_size} ${settings.orientation}`;
  const scale = Math.max(70, Math.min(130, Number(settings.content_scale) || 100)) / 100;

  return (
    <>
      <style>{`
        @page { size: ${paperSize}; margin: ${settings.margin_top}mm ${settings.margin_right}mm ${settings.margin_bottom}mm ${settings.margin_left}mm; }
        @media print {
          body { background: white; }
          .no-print { display: none !important; }
          /* @page already applies the trim; zero the on-screen padding
             so we don't margin the content twice on paper. */
          .rx-card { padding: 0 !important; }
        }
        body { background: #f5f5f5; margin: 0; }
        .rx-card {
          width: ${settings.paper_size === "A5" ? (settings.orientation === "landscape" ? "210mm" : "148mm") : (settings.orientation === "landscape" ? "297mm" : "210mm")};
          min-height: ${settings.paper_size === "A5" ? (settings.orientation === "landscape" ? "148mm" : "210mm") : (settings.orientation === "landscape" ? "210mm" : "297mm")};
          margin: 0 auto;
          box-sizing: border-box;
          padding: ${settings.margin_top}mm ${settings.margin_right}mm ${settings.margin_bottom}mm ${settings.margin_left}mm;
          font-family: 'Calibri', 'Carlito', 'Helvetica', 'Arial', sans-serif;
          color: #0f172a;
          background: white;
          display: flex;
          flex-direction: column;
        }
        .rx-body { transform: scale(${scale}); transform-origin: top left; width: ${100 / scale}%; }
        .rx-header {
          display: flex;
          align-items: center;
          gap: 5mm;
          border-bottom: 1.2px solid #0e3b6e;
          padding-bottom: 1.5mm;
          margin-bottom: 2mm;
        }
        .rx-logo { max-height: 12mm; max-width: 22mm; }
        .rx-brand-name { font-size: 15pt; font-weight: 700; color: #0e3b6e; }
        .rx-brand-sub { font-size: 8pt; color: #475569; margin-top: 0.3mm; }
        .rx-panel {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1.5mm 4mm;
          border: 0.6px solid #cbd5e1;
          background: #f8fafc;
          padding: 1.5mm 2.5mm;
          border-radius: 1.2mm;
          font-size: 9pt;
        }
        .rx-panel-key {
          display: block;
          font-size: 6.5pt;
          font-weight: 700;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.4px;
          margin-bottom: 0.3mm;
        }
        .rx-panel-val { font-weight: 600; font-size: 9pt; }
        .rx-uhid-val { font-family: 'Courier New', monospace; font-weight: 800; color: #0e3b6e; }
        .rx-section {
          margin-top: 2mm;
          break-inside: avoid;
        }
        .rx-section-title {
          font-size: 9pt;
          font-weight: 800;
          color: #0e3b6e;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-bottom: 1px solid #0e3b6e;
          padding-bottom: 0.4mm;
          margin-bottom: 1mm;
        }
        .rx-section-title .rx-glyph {
          display: inline-block;
          background: #0e3b6e;
          color: white;
          padding: 0.2mm 1.5mm;
          border-radius: 0.8mm;
          margin-right: 1.2mm;
          font-family: 'Times New Roman', serif;
        }
        .rx-lines {
          background:
            repeating-linear-gradient(
              to bottom,
              transparent 0,
              transparent 7mm,
              #dbe2ea 7mm,
              #dbe2ea 7.12mm
            );
        }
        .rx-lines-lg { min-height: 35mm; }
        .rx-lines-md { min-height: 14mm; }
        .rx-lines-sm { min-height: 9mm; }
        .rx-footer {
          margin-top: auto;
          padding-top: 3mm;
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          font-size: 8.5pt;
          color: #64748b;
        }
        .rx-signline {
          display: inline-block;
          border-top: 1px solid #0f172a;
          min-width: 60mm;
          text-align: center;
          padding-top: 1mm;
          font-size: 8.5pt;
          font-weight: 600;
          color: #0f172a;
        }
        /* Compact RE/LE grid — matches the tightened section rhythm above. */
        .rx-eye-grid {
          display: grid;
          grid-template-columns: 28mm 1fr 1fr;
          border: 0.6px solid #cbd5e1;
          border-radius: 1.2mm;
          overflow: hidden;
          font-size: 8.5pt;
        }
        .rx-eye-grid > div {
          padding: 1.2mm 2mm;
          border-bottom: 0.4px solid #e2e8f0;
          border-right: 0.4px solid #e2e8f0;
          min-height: 5.5mm;
        }
        .rx-eye-grid > div:nth-child(3n) { border-right: 0; }
        .rx-eye-grid > .rx-eye-head {
          background: #eef2f7;
          font-weight: 800;
          font-size: 7.5pt;
          color: #0e3b6e;
          text-transform: uppercase;
          letter-spacing: 0.4px;
        }
        .rx-eye-grid > .rx-eye-row-key {
          background: #f8fafc;
          font-weight: 700;
          font-size: 7.5pt;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          color: #475569;
        }
      `}</style>

      <div className="rx-card">
        <div className="rx-body">
        {showHeader && (
          <div className="rx-header">
            {logo && <img src={logo} alt="" className="rx-logo" />}
            <div>
              <div className="rx-brand-name">{hospital?.name || branch?.name || "CareO Eye"}</div>
              <div className="rx-brand-sub">
                {branch?.address || hospital?.address || ""}
                {(branch?.phone || hospital?.phone) ? ` · ${branch?.phone || hospital?.phone}` : ""}
                {branch?.reg_no ? ` · Reg No: ${branch.reg_no}` : ""}
              </div>
            </div>
            <div style={{ marginLeft: "auto", textAlign: "right" }}>
              <div style={{ fontSize: "8.5pt", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 700 }}>OPD Prescription Card</div>
              <div style={{ fontSize: "8.5pt", color: "#64748b" }}>{dateStr} · {timeStr}</div>
            </div>
          </div>
        )}

        {/* Identity panel — 4 columns */}
        <div className="rx-panel">
          <div><span className="rx-panel-key">UHID</span><span className="rx-panel-val rx-uhid-val">{patient.uhid || "—"}</span></div>
          <div><span className="rx-panel-key">Name</span><span className="rx-panel-val">{patient.name || "—"}</span></div>
          <div><span className="rx-panel-key">Age / Sex</span><span className="rx-panel-val">{ageSex}</span></div>
          <div><span className="rx-panel-key">Mobile</span><span className="rx-panel-val">{patient.mobile || "—"}</span></div>
          <div><span className="rx-panel-key">Doctor</span><span className="rx-panel-val">{doctor}</span></div>
          <div><span className="rx-panel-key">Dept</span><span className="rx-panel-val">Ophthalmology</span></div>
          <div style={{ gridColumn: "span 2" }}>
            <span className="rx-panel-key">Address</span>
            <span className="rx-panel-val" style={{ fontSize: "8.5pt" }}>{patient.address || "—"}</span>
          </div>
        </div>

        {/* History */}
        <div className="rx-section">
          <div className="rx-section-title">History</div>
          <div className="rx-lines rx-lines-md" />
        </div>

        {/* Visual acuity — RE/LE × (Unaided / Pinhole / With correction / Near).
            Columns are flipped clinician-facing: patient's LEFT eye (OS) sits
            on the doctor's LEFT and patient's RIGHT eye (OD) sits on the
            doctor's RIGHT — matching how it's written on the paper card. */}
        <div className="rx-section">
          <div className="rx-section-title">Visual Acuity</div>
          <div className="rx-eye-grid">
            <div className="rx-eye-head" />
            <div className="rx-eye-head">Left Eye (OS)</div>
            <div className="rx-eye-head">Right Eye (OD)</div>
            <div className="rx-eye-row-key">Unaided</div><div /><div />
            <div className="rx-eye-row-key">Pinhole</div><div /><div />
            <div className="rx-eye-row-key">With correction</div><div /><div />
            <div className="rx-eye-row-key">Near vision</div><div /><div />
          </div>
        </div>

        {/* Refraction — Sph / Cyl / Axis / Add + IPD.
            Rows are ordered LE (OS) first, RE (OD) second so it matches the
            Visual Acuity table above and how eye Rx is traditionally written. */}
        <div className="rx-section">
          <div className="rx-section-title">Refraction <span style={{ fontSize: "8pt", fontWeight: 400, color: "#64748b" }}>· IPD: ______ mm</span></div>
          <div className="rx-eye-grid" style={{ gridTemplateColumns: "30mm 1fr 1fr 1fr 1fr" }}>
            <div className="rx-eye-head" />
            <div className="rx-eye-head">Sph</div>
            <div className="rx-eye-head">Cyl</div>
            <div className="rx-eye-head">Axis</div>
            <div className="rx-eye-head">Add</div>
            <div className="rx-eye-row-key">LE (OS)</div><div /><div /><div /><div />
            <div className="rx-eye-row-key">RE (OD)</div><div /><div /><div /><div />
          </div>
        </div>

        {/* Examination — slit-lamp / IOP / fundus / adnexa */}
        <div className="rx-section">
          <div className="rx-section-title">Examination <span style={{ fontSize: "8pt", fontWeight: 400, color: "#64748b" }}>· Slit lamp / IOP / Fundus / Adnexa</span></div>
          <div className="rx-lines rx-lines-sm" />
        </div>

        {/* Diagnosis & Plan */}
        <div className="rx-section">
          <div className="rx-section-title">Diagnosis &amp; Plan</div>
          <div className="rx-lines rx-lines-md" />
        </div>

        {/* Pain assessment */}
        <div className="rx-section">
          <div className="rx-section-title">Pain Assessment <span style={{ fontSize: "8pt", fontWeight: 400, color: "#64748b" }}>· Score /10 &nbsp; Site: ______ &nbsp; Duration: ______ &nbsp; Character: ______</span></div>
          <div className="rx-lines rx-lines-sm" />
        </div>

        {/* Nutrition assessment (MUST) */}
        <div className="rx-section">
          <div className="rx-section-title">Nutrition Assessment (MUST) <span style={{ fontSize: "8pt", fontWeight: 400, color: "#64748b" }}>· BMI: ______ &nbsp; Wt loss (6mo): ______ &nbsp; Acute illness: ______ &nbsp; MUST score: ______</span></div>
          <div className="rx-lines rx-lines-sm" />
        </div>

        {/* Prescription (Rx) — biggest ruled block */}
        <div className="rx-section">
          <div className="rx-section-title"><span className="rx-glyph">℞</span>Prescription (Rx)</div>
          <div className="rx-lines rx-lines-lg" />
        </div>

        <div className="rx-footer">
          <div>Follow-up: __________________</div>
          <div className="rx-signline">{doctor}<div style={{ fontSize: "7.5pt", color: "#64748b" }}>Signature</div></div>
        </div>
        </div>
      </div>

      <div className="no-print" style={{ position: "fixed", top: 8, right: 8, display: "flex", alignItems: "center", gap: 8 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, background: "white", border: "1px solid #cbd5e1", borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer" }} data-testid="rx-card-letterhead-toggle">
          <input
            type="checkbox"
            checked={letterhead}
            onChange={(e) => setLetterhead(e.target.checked)}
            style={{ margin: 0 }}
          />
          Letterhead mode <span style={{ color: "#64748b" }}>(hide header)</span>
        </label>
        <button onClick={() => window.print()} style={{ padding: "6px 12px", background: "#111", color: "#fff", borderRadius: 6, fontSize: 12, border: 0, cursor: "pointer" }} data-testid="rx-card-print-btn">
          Print prescription card
        </button>
      </div>
    </>
  );
}
